import type {
  EmrCodeDetails,
  EmrCodeItem,
  ProcedureItem,
} from 'features/Procedure/state/procedure-api.type'
import { ProcedureTypeCode } from 'features/Procedure/state/procedure.type'
import { extractDynamicFieldValues } from './dynamic-fields.utils'
import type { FormValues } from './schema'

export enum ProcedureTab {
  CPT = 'cpt',
  ICD = 'icd',
}

export const EMPTY_PROCEDURE_CODE = 999999

export const defaultInitialValues: FormValues = {
  activeTab: ProcedureTab.CPT,
  cpt4_code: null,
  icd10_codes: [],
  procedure_units: undefined,
  units: 1,
  price: 0,
  discount_type_id: null,
  dynamic_fields: [],
  dynamic_field_values: {},
}

const parseJustifyCodes = (justifyCodes: string[]): EmrCodeItem[] => {
  return justifyCodes
    .filter((code) => code.startsWith(ProcedureTypeCode.ICD10))
    .map((code) => {
      const [, icd10Code] = code.split('|')
      return {
        code: icd10Code.replace(/\s+/g, ''),
        code_type: ProcedureTypeCode.ICD10,
        code_text: '',
        code_text_short: '',
        active: 1,
        id: 0,
      }
    })
}

export const formatJustifyCodes = (icd10Codes: EmrCodeItem[]): string[] => {
  return icd10Codes.map((icd10) => `${icd10.code_type}|${icd10.code.replace(/\s+/g, '')}`)
}

export const getValuesOnCptSelect = (
  currentValues: FormValues,
  newCpt: EmrCodeItem | null,
): FormValues => {
  return {
    ...defaultInitialValues,
    activeTab: currentValues.activeTab,
    discount_type_id: currentValues.discount_type_id,
    cpt4_code: newCpt,
    price:
      newCpt && typeof newCpt.default_price === 'number' && newCpt.default_price >= 0
        ? newCpt.default_price
        : 0,
  }
}

export const applyCptDetails = (currentValues: FormValues, details: EmrCodeDetails): FormValues => {
  const selectedCpt = currentValues.cpt4_code as EmrCodeItem | null

  if (!selectedCpt || selectedCpt.id !== details.id) {
    return currentValues
  }

  return {
    ...currentValues,
    icd10_codes: details.icd_items.map((item) => ({
      ...item,
      code_text: item.code_text?.trim() ?? '',
      code_text_short: item.code_text_short?.trim() ?? '',
    })),
    dynamic_fields: details.fields,
    dynamic_field_values: {},
  }
}

export const applyCptDetailsForEdit = (
  currentValues: FormValues,
  details: EmrCodeDetails,
  procedure: ProcedureItem,
): FormValues => {
  const selectedCpt = currentValues.cpt4_code as EmrCodeItem | null

  if (!selectedCpt || selectedCpt.id !== details.id) {
    return currentValues
  }

  return {
    ...currentValues,
    dynamic_fields: details.fields,
    dynamic_field_values: extractDynamicFieldValues(
      details.fields,
      procedure as unknown as Record<string, unknown>,
    ),
  }
}

export const getInitialValues = (
  procedure?: ProcedureItem | null,
  defaultDiscountTypeId?: string | null,
): FormValues => {
  if (!procedure) {
    return {
      ...defaultInitialValues,
      discount_type_id: defaultDiscountTypeId || null,
    }
  }

  const isCptProcedure = procedure.code_type === ProcedureTypeCode.CPT4

  if (!isCptProcedure) {
    return {
      ...defaultInitialValues,
      activeTab: ProcedureTab.ICD,
      icd10_codes: parseJustifyCodes(procedure.justify_codes || []),
      discount_type_id: procedure.discount_type || defaultDiscountTypeId || null,
    }
  }

  const cpt4CodeItem: EmrCodeItem = {
    code: procedure.code,
    code_type: procedure.code_type,
    code_text: procedure.code_text || '',
    code_text_short: '',
    active: 1,
    id: procedure.code_id ?? 0,
  }

  const icd10CodeItems = parseJustifyCodes(procedure.justify_codes || [])

  return {
    ...defaultInitialValues,
    activeTab: ProcedureTab.CPT,
    cpt4_code: cpt4CodeItem,
    icd10_codes: icd10CodeItems,
    procedure_units: procedure.procedure_units || undefined,
    units: procedure.units,
    price: procedure.fee,
    discount_type_id: procedure.discount_type || defaultDiscountTypeId || null,
  }
}
